// Payment integration: PayPal is Belivah's only payment gateway.
// Buyers can pay with a PayPal balance or an eligible debit/credit card (PayPal's card button).
// Orders are created and captured on the backend; the wallet is credited only after PayPal confirms the capture.
import {
  cancelWalletTopup,
  confirmWalletTopupPayPal,
  createWalletTopupPayPalOrder,
  getPaymentsConfig,
  getWalletTopupStatus,
  walletTopupInit,
  WalletTopupPaymentStatus,
} from './secureFunctions';

const ENV_PAYPAL_CLIENT_ID = (process.env.REACT_APP_PAYPAL_CLIENT_ID || '').trim();

let clientIdPromise: Promise<string> | null = null;

/** The PayPal client ID is public; prefer the server value so keys are managed in one place. */
export const getPayPalClientId = (): Promise<string> => {
  if (!clientIdPromise) {
    clientIdPromise = getPaymentsConfig()
      .then((cfg) => (cfg.paypalConfigured && cfg.clientId ? cfg.clientId : ENV_PAYPAL_CLIENT_ID))
      .catch(() => ENV_PAYPAL_CLIENT_ID)
      .then((id) => {
        if (!id) clientIdPromise = null;
        return id;
      });
  }
  return clientIdPromise;
};

export const loadPayPalScript = async (): Promise<void> => {
  const clientId = await getPayPalClientId();
  if (!clientId) throw new Error('PayPal payments are not available yet. Please try again later.');
  if ((window as any).paypal) return;

  await new Promise<void>((resolve, reject) => {
    const existing = Array.from(document.scripts).find((s) => s.src.includes('www.paypal.com/sdk/js'));
    if (existing) {
      let waited = 0;
      const iv = setInterval(() => {
        if ((window as any).paypal) { clearInterval(iv); resolve(); }
        else if ((waited += 100) > 15000) { clearInterval(iv); reject(new Error('PayPal failed to initialise')); }
      }, 100);
      return;
    }
    const params = new URLSearchParams({
      'client-id': clientId,
      currency: 'USD',
      intent: 'capture',
      components: 'buttons',
      'enable-funding': 'card',
    });
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
    script.async = true;
    script.dataset.namespace = 'paypal';
    const timeout = setTimeout(() => reject(new Error('Timed out loading PayPal. Check your connection.')), 15000);
    script.onload = () => {
      clearTimeout(timeout);
      if ((window as any).paypal) resolve(); else reject(new Error('PayPal failed to initialise'));
    };
    script.onerror = () => { clearTimeout(timeout); reject(new Error('Failed to load PayPal.')); };
    document.body.appendChild(script);
  });
};

export interface WalletTopupOptions {
  amountUSD: number;
  onStatus: (status: WalletTopupPaymentStatus | { status: 'processing' | 'created'; amountUsd: number }) => void;
  onError: (message: string) => void;
}

const describeError = (e: any): string => {
  const code = String(e?.code || e?.message || '');
  if (code.includes('PAYPAL_NOT_CONFIGURED')) return 'PayPal payments are not available yet. Please try again later.';
  if (code.includes('PAYMENT_DECLINED')) return 'Your payment method was declined. Please try another card or PayPal funding source.';
  if (code.includes('INVALID')) return 'Please enter an amount between $1 and $10,000.';
  return e?.message ? String(e.message) : 'Payment could not be completed. You have not been charged.';
};

/** Renders PayPal (and card) buttons into the container and drives the server-verified top-up flow. */
export const initiateWalletTopupPayPal = async (containerId: string, opts: WalletTopupOptions) => {
  const init = await walletTopupInit({ amountUsd: opts.amountUSD });
  const intentId = init.intentId;
  opts.onStatus({ status: 'created', amountUsd: opts.amountUSD });

  await loadPayPalScript();
  const paypal = (window as any).paypal;
  const container = document.getElementById(containerId);
  if (!container) throw new Error(`PayPal container '#${containerId}' not found`);
  container.innerHTML = '';

  await paypal.Buttons({
    style: { layout: 'vertical', shape: 'pill', label: 'pay' },
    createOrder: async () => {
      const res = await createWalletTopupPayPalOrder({ intentId });
      return res.orderId;
    },
    onApprove: async (data: any, actions: any) => {
      opts.onStatus({ status: 'processing', amountUsd: opts.amountUSD });
      try {
        const res = await confirmWalletTopupPayPal({ orderId: data.orderID });
        if (res.payment) opts.onStatus(res.payment);
        else opts.onError('We could not confirm this payment yet. Check your wallet history shortly.');
      } catch (e: any) {
        if (String(e?.code || e?.message || '').includes('PAYMENT_DECLINED')) {
          return actions.restart();
        }
        opts.onError(describeError(e));
      }
    },
    onCancel: async () => {
      try {
        const res = await cancelWalletTopup({ intentId, reason: 'cancelled' });
        if (res.payment) opts.onStatus(res.payment);
      } catch {
        opts.onError('Payment cancelled.');
      }
    },
    onError: (err: any) => {
      cancelWalletTopup({ intentId, reason: 'error' }).catch(() => undefined);
      opts.onError(describeError(err));
    },
  }).render(`#${containerId}`);

  return intentId;
};

export const refreshWalletTopupStatus = async (intentId: string) => (await getWalletTopupStatus(intentId)).payment;
// Calculate video ad revenue
export const calculateVideoAdRevenue = (impressions: number, clicks: number): number => {
  const CPM = 2.0; // $2 per 1000 impressions
  const CPC = 0.5; // $0.50 per click
  
  const impressionRevenue = (impressions / 1000) * CPM;
  const clickRevenue = clicks * CPC;
  
  return impressionRevenue + clickRevenue;
};

// Calculate music royalty
export const calculateMusicRoyalty = (views: number): number => {
  const RATE_PER_VIEW = 0.001; // $0.001 per view with music
  return views * RATE_PER_VIEW;
};

// Split revenue between creator and platform
export const splitRevenue = (totalRevenue: number, creatorSharePercent: number = 60) => {
  const creatorShare = totalRevenue * (creatorSharePercent / 100);
  const platformShare = totalRevenue - creatorShare;
  
  return { creatorShare, platformShare };
};

// Update user earnings
export const updateUserEarnings = async (
  userId: string,
  videoRevenue: number = 0,
  musicRevenue: number = 0
) => {
  console.warn('updateUserEarnings is deprecated in VPS mode.', { userId, videoRevenue, musicRevenue });
};
